import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import Dashboard from '../../../Components/Dashboard'
import { formatBeep, formatBeepShort } from '../_shared/datetime'
import {
  GlobalHomeStyles, PageWrap, Header, Logo, HeaderStatus, StatusDot,
  NavBar, NavTab, MainContent,
  UploadHero, DropZone, UploadBtn, UploadHint, ResetBtn,
  StatsGrid, StatCard, StatLabel, StatValue, StatSub,
  Card, CardHeader, CardBadge, CardBody,
  ConsultaGrid, ConsultaCol,
  ScanLabel, ScanInputWrap, ScanInput, ScanIcon,
  NfChipsRow, NfChip,
  BigResult, BigResultHeader, BigNfLabel, BigSeqVal,
  BigFieldsGrid, BigField, BigFieldLabel, BigFieldVal,
  BigMetricsRow, BigMetric, BigMetricLabel, BigMetricVal, BipBadge,
  DetailSection, DetailHeader, DetailRow, DetailKey, DetailVal,
  EmptyPlaceholder, NotFoundBox, NoDataScreen, GoUploadBtn,
  Toolbar, SearchInput, FilterSelect, TableWrap, StyledTable, RowCount, Tag,
  Toast,
  EntregasToolbar, StatusChip,
  AccordionList, AccordionItem, AccordionHeader, AccordionStatusBadge,
  AccordionBody, VolumeGrid, VolumeChip,
  BoxesGrid, BoxCard, BoxCardHeader, BoxStatsRow, BoxStatCell,
  BoxDriversList, BoxDriverRow,
  BoxOrdersModalOverlay, BoxOrdersModalBox, BoxOrdersModalHeader, BoxOrdersModalBody,
  ConfWrap, ConfScanCard, ConfScanInput, ConfFeedback,
  ConfBoxBanner, ConfPanel, ConfVolumesList, ConfVolumeRow, ConfResetBtn,
  BoxModalBtn,
} from './style'
import { store } from '../../../store'
import { apiLabs, apiSantoLog } from '../../../Services/api'
import history from '../../../Services/history'
import Denied from '../../../Components/Denied'
import FeedbackModal, { FeedbackExtra } from '../_shared/FeedbackModal'
import { useAudioUnlock, playErrorBeep, playWarningBeep, playSuccessBeep } from '../_shared/sounds'
import { useScannerInput, useIsCoarsePointer, HIDDEN_INPUT_STYLE } from '../_shared/scanner'
import { normalizeRastreio } from '../_shared/rastreio'
import EmbarkBoxModal from './EmbarkBoxModal'

// ─── TABS CONFIGURATION ───────────────────────────────────────────────────────
// "Importar" foi removido — agora a importação acontece na tela Pedidos (Cadastros).
const TABS = [
  { id: 'consulta',   label: '🔍 Consulta QR' },
  { id: 'conference', label: '✅ Conferência' },
  { id: 'entregas',   label: '📦 Entregas' },
  { id: 'boxes',      label: '🗃 Boxes' },
]

const BACKEND_PER_PAGE = 1000
const ENTREGAS_STATUSES = 'aguardando_recebimento,coletado_parcial,coletado'

/**
 * Converte um pedido vindo do backend (snake_case) para o formato "planilha"
 * que toda a UI atual desta página espera (chaves em PT com espaços).
 * Mantém __id pra referência ao UUID original quando precisarmos.
 */
function apiOrderToPlanilha(o) {
  const driverDisplay = o.driver_name
    ? (o.driver_phone ? `${o.driver_name} ${o.driver_phone}` : o.driver_name)
    : (o.driver_raw || null)
  return {
    __id: o.id,
    __status: o.status,
    __driver_id: o.driver_id || null,
    __volumes_scanned: Number(o.volumes_scanned_count) || 0,
    'Número': o.order_number,
    'Destinatário': o.recipient_name,
    'E-mail': o.email,
    'Logradouro': o.street,
    'Complemento': o.complement,
    'Bairro': o.neighborhood,
    'Cidade': o.city,
    'UF': o.state_uf,
    'CEP': o.cep,
    'Motorista': driverDisplay,
    'Rota': o.route,
    'Sequência': o.sequence_order,
    'Peso (KG)': o.weight_kg,
    'Quantidade de Volumes': o.volumes_count,
    'Situação': o.situation,
    'Classificação': o.classification,
    'Previsão Início - Data / Hora': o.forecast_start,
    'Previsão Fim - Data / Hora': o.forecast_end,
    'Observação': o.observation,
    'Tags': o.tags,
    'Recebedor': o.recipient_signer,
  }
}

// ─── SORTABLE TABLE COLUMNS ───────────────────────────────────────────────────
const TABLE_COLUMNS = [
  { key: null, label: 'Vol. Bipado', align: 'center' },
  { key: 'Número', label: 'NF' },
  { key: 'Motorista', label: 'Motorista' },
  { key: 'Destinatário', label: 'Destinatário' },
  { key: 'Bairro', label: 'Bairro' },
  { key: 'Cidade', label: 'Cidade' },
  { key: 'CEP', label: 'CEP' },
  { key: 'Rota', label: 'Rota' },
  { key: 'Sequência', label: 'Seq.', align: 'center' },
  { key: 'Peso (KG)', label: 'Peso', align: 'right' },
  { key: 'Situação', label: 'Situação' },
  { key: 'Classificação', label: 'Classificação' },
]

// ─── PURE HELPER FUNCTIONS ────────────────────────────────────────────────────

/** Extrai nome legível do motorista removendo o número de telefone */
function extractDriverName(rawName) {
  if (!rawName) return ''
  return rawName.replace(/\s*\+55\s*[\d\s]+$/, '').trim()
}

/** Extrai número de telefone do campo de motorista */
function extractDriverPhone(rawName) {
  const match = rawName.match(/\+55\s*([\d\s]+)$/)
  return match ? '+55 ' + match[1].trim() : ''
}

/** Remove CPF ao final do nome do destinatário */
function cleanRecipientName(rawName) {
  return (rawName || '').replace(/\s*-\s*\d{11}\s*$/, '').trim()
}

/** Formata CEP para o padrão 00000-000 */
function formatCep(rawCep) {
  if (!rawCep) return '—'
  const digits = String(rawCep).replace(/\D/g, '').padStart(8, '0')
  return digits.slice(0, 5) + '-' + digits.slice(5)
}

/** Formata data/hora de forma legível */
function formatDate(dateValue) {
  if (!dateValue) return '—'
  if (dateValue instanceof Date) {
    return dateValue.toLocaleDateString('pt-BR', {
      day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
    })
  }
  return String(dateValue)
}

/**
 * Tenta extrair o número da NF de um código de barras/QR.
 * Ex: ALEF642141V1 → "642141"
 */
function extractNfFromBarcode(barcode) {
  // Padrão: dígitos antes do 'V' seguido de número
  const matchBeforeV = barcode.match(/(\d+)(?=V\d)/i)
  if (matchBeforeV) return matchBeforeV[1]
  // Fallback: pega o primeiro grupo de dígitos
  const matchDigits = barcode.match(/\d+/)
  return matchDigits ? matchDigits[0] : null
}

/** Extrai o número do volume do código (ex: ALEF642141V3 → 3) */
function extractVolumeFromBarcode(barcode) {
  const match = barcode.match(/V(\d+)\s*$/i)
  return match ? parseInt(match[1]) : null
}

// ─── VOLUME SHAPE HELPERS ─────────────────────────────────────────────────
// Status do volume: aguardando_recebimento → coletado (conferido) → embarcado.
// Após a reintrodução da tabela santolog_box_volume (jun/26), volumes coletados
// trazem a info do box via `box_volume.box` aninhado. Mantemos fallbacks pra
// outras shapes (box direto, box_id solto, expected_box etc).
function volumeIsEmbarked(v) { return v?.status === 'embarcado' }
function volumeIsColetado(v) { return v?.status === 'coletado' }
function volumeInBox(v)      { return volumeIsColetado(v) }
function volumeActiveInBox(v){ return volumeIsColetado(v) }
// "Bipado" pra fins de contagem na Big Card da Consulta QR:
// inclui quem já passou pelo first-bipe (chegada no galpão) E também
// volumes já coletados/embarcados (que necessariamente passaram pelo bipe).
function volumeFirstBipado(v) {
  if (!v) return false
  if (v.first_scanned_at != null) return true
  return volumeIsColetado(v) || volumeIsEmbarked(v)
}
function volumeNumber(v) {
  const n = v?.volume ?? v?.volume_number
  return Number.isFinite(Number(n)) ? Number(n) : null
}
// Resolve nome/id do box olhando 5 shapes possíveis:
//   1) v.box_volume.box.{name,id}    ← shape mais provável (relação santolog_box_volume)
//   2) v.box.{name,id}                ← shape direta (box embutido no volume)
//   3) v.box_name / v.box_id          ← shape "achatada"
//   4) v.expected_box.{name,id}       ← /volume/by-rastreio adicionou esse campo
//   5) v.boxVolume.box.{name,id}      ← camelCase variant
function volumeBoxName(v) {
  return v?.box_volume?.box?.name
      || v?.boxVolume?.box?.name
      || v?.box?.name
      || v?.expected_box?.name
      || v?.box_name
      || null
}
function volumeBoxId(v) {
  return v?.box_volume?.box_id
      || v?.box_volume?.box?.id
      || v?.boxVolume?.box?.id
      || v?.box?.id
      || v?.expected_box?.id
      || v?.box_id
      || null
}
// Nome do box do volume. Tenta as shapes aninhadas; se só houver o box_id
// (caso comum: o volume traz box_volume.box_id, mas o NOME do box vem em
// order.driver_boxes[]), cruza o id com os driver_boxes do pedido.
function volumeBoxNameFor(v, driverBoxes) {
  const direct = volumeBoxName(v)
  if (direct) return direct
  const boxes = Array.isArray(driverBoxes) ? driverBoxes : []
  const bid = volumeBoxId(v)
  if (bid) {
    const found = boxes.find((b) => b && String(b.id) === String(bid))
    if (found && found.name) return found.name
  }
  // Sem link explícito volume→box: se o volume já passou por box (coletado/
  // embarcado) e o pedido tem UM único box, é necessariamente esse.
  if (volumeIsColetado(v) || volumeIsEmbarked(v)) {
    const named = boxes.filter((b) => b && b.name)
    if (named.length === 1) return named[0].name
  }
  return null
}

/** Resolve a cor do status para o componente Tag */
function getSituacaoVariant(situacao) {
  if (!situacao) return 'recepcionada'
  if (situacao.includes('Aguardando')) return 'aguardando'
  if (situacao.includes('Aceita')) return 'aceita'
  if (situacao.includes('Finalizada') || situacao.includes('Entregue')) return 'finalizada'
  return 'recepcionada'
}

/** Label resumida para exibição da situação na tabela */
function getSituacaoLabel(situacao) {
  if (!situacao) return situacao
  if (situacao.includes('Aguardando')) return 'Ag. Aceite'
  if (situacao.includes('Aceita')) return 'Em Trânsito'
  if (situacao.includes('Recepcionada')) return 'Não Enviada'
  return situacao
}

/** Hook simples de debounce para valores de input */
function useDebounced(value, delay) {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(timer)
  }, [value, delay])
  return debounced
}

// ─── COMPONENT ────────────────────────────────────────────────────────────────
export default function SantoLogHome(props) {

  const { location: { idPage } } = props
  const [auth, setAuth] = useState(null)
  async function verifyAuth() {
    const { profile } = store.getState().user
    const res = await apiLabs.post('valid_key', { id: profile.id, menu_id: idPage })
    if (res.data.message === "User not authorized") {
      return setAuth('denied')
    }
    if (res.data.message === "User authorized") {
      return setAuth('authorized')
    }
    return history.push({ pathname: '/', state: { from: props.location } })
  }

  // ── State: dados principais ─────────────────────────────────────────────────
  const [deliveries, setDeliveries] = useState([])       // pedidos vindos do backend (formato planilha)
  const [ordersRaw, setOrdersRaw] = useState([])         // pedidos crus (snake_case + volumes[])
  const [bippedVolumes, setBippedVolumes] = useState({}) // { nfStr: Set<number> } — hidratado dos volumes
  const [activeTab, setActiveTab] = useState('consulta')
  const [loadingOrders, setLoadingOrders] = useState(false)

  // ── State: Entregas (accordion) ─────────────────────────────────────────────
  const [expandedOrderId, setExpandedOrderId] = useState(null)
  const [entregasFilter, setEntregasFilter] = useState('all') // 'all' | 'parcial' | 'aguardando' | 'coletado'

  // ── State: modal "pedidos do box" (aba Boxes) ───────────────────────────────
  // boxOrdersModal = null ou { name, totalScanned, totalExpected, rawOrders }
  const [boxOrdersModal, setBoxOrdersModal] = useState(null)
  const [boxOrdersExpandedId, setBoxOrdersExpandedId] = useState(null)

  // ── State: atalho "Embarcar Box" (aba Boxes) ────────────────────────────────
  // embarkBoxModal = null ou { id, name } do box escolhido no card
  const [embarkBoxModal, setEmbarkBoxModal] = useState(null)
  useEffect(() => {
    if (!boxOrdersModal) return
    const onKey = (e) => { if (e.key === 'Escape') setBoxOrdersModal(null) }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [boxOrdersModal])

  // ── State: cache do último box conhecido por pedido ──────────────────────
  const [lastBoxByOrder, setLastBoxByOrder] = useState({}) // { orderId → boxName } p/ exibir

  // ── State: Conferência (nova aba box-first) ────────────────────────────────
  // Modal genérico com 3 kinds: 'error' | 'warning' | 'success'
  const initialConfModal = {
    open: false, kind: 'error', title: '', message: '',
    order: null, correct_box: null, finalize: false,
  }
  const [confBox, setConfBox] = useState(null)         // box atualmente em conferência
  const [confVolumes, setConfVolumes] = useState([])   // lista de volumes esperados
  const [confScanValue, setConfScanValue] = useState('')
  const [confBusy, setConfBusy] = useState(false)
  const [confFeedback, setConfFeedback] = useState({ kind: null, message: '' })
  const [confModal, setConfModal] = useState(initialConfModal)

  // ── State: tabela de entregas ────────────────────────────────────────────────
  const [tableSearch, setTableSearch] = useState('')
  const [filterSituacao, setFilterSituacao] = useState('')
  const [filterMotorista, setFilterMotorista] = useState('')
  const [filterRota, setFilterRota] = useState('')
  const [sortColumn, setSortColumn] = useState('Número')
  const [sortAscending, setSortAscending] = useState(true)

  // ── State: consulta QR ───────────────────────────────────────────────────────
  const [scanValue, setScanValue] = useState('')
  const [nfValue, setNfValue] = useState('')
  const [scanChips, setScanChips] = useState(null)  // { rawCode, nf, volume }
  const [consultaResult, setConsultaResult] = useState(null) // { type: 'found'|'notFound', ...data }
  const [bigResult, setBigResult] = useState(null)

  // ── State: drag & drop na upload zone ───────────────────────────────────────
  // ── State: toast notification ────────────────────────────────────────────────
  const [toast, setToast] = useState({ message: '', isError: false, visible: false })

  // ── Refs ─────────────────────────────────────────────────────────────────────
  const autoClearTimerRef = useRef(null)
  const toastTimerRef = useRef(null)
  const scanInputRef = useRef(null)        // input principal de rastreio (Consulta QR)
  const confScanInputRef = useRef(null)    // input da aba Conferência

  // Cache em memória dos rastreios que já chamaram POST /volume/first-bipe
  // nesta sessão. Evita re-chamar a API quando o operador rebipa o mesmo
  // rastreio. Volátil — limpa ao recarregar / sair da tela.
  const firstBipeCacheRef = useRef(new Set())

  // Destrava o AudioContext compartilhado no primeiro gesture. Idempotente.
  useAudioUnlock()

  // ─── SCANNER (arming pattern) ───────────────────────────────────────────────
  // Arma o input ATIVO da aba atual sem abrir teclado virtual no Android.
  // armKey combina activeTab + confBox?.id pra cobrir transições:
  //   - troca de aba (consulta ↔ conferência)
  //   - dentro da conferência: troca "bipar box" → "bipar volume"
  const isCoarse = useIsCoarsePointer()
  const { arm: armScanner } = useScannerInput({
    getInput: () => activeTab === 'consulta'  ? scanInputRef.current
                  : activeTab === 'conference' ? confScanInputRef.current
                  : null,
    getDisabled: () => {
      if (auth !== 'authorized') return true
      if (boxOrdersModal) return true
      if (embarkBoxModal) return true
      if (confModal.open) return true
      return activeTab !== 'consulta' && activeTab !== 'conference'
    },
    armKey: `${activeTab}:${confBox?.id || 'no-box'}`,
  })

  // ── Debounced search para filtrar tabela sem rerender a cada tecla ───────────
  const debouncedSearch = useDebounced(tableSearch, 200)

  // ─── TOAST ─────────────────────────────────────────────────────────────────
  /** Exibe uma notificação temporária no canto inferior direito */
  const showToast = useCallback((message, isError = false) => {
    clearTimeout(toastTimerRef.current)
    setToast({ message, isError, visible: true })
    toastTimerRef.current = setTimeout(
      () => setToast(prev => ({ ...prev, visible: false })),
      3500,
    )
  }, [])

  // ─── BACKEND FETCH ─────────────────────────────────────────────────────────
  /**
   * Busca pedidos no backend (aguardando_recebimento / coletado_parcial / coletado),
   * trazendo a lista de volumes aninhada. Lê todas as páginas em paralelo antes
   * de remontar a UI.
   */
  const fetchOrders = useCallback(async () => {
    setLoadingOrders(true)
    try {
      const baseParams = {
        status: ENTREGAS_STATUSES,
        include_volumes: true,
        perPage: BACKEND_PER_PAGE,
      }
      const first = await apiSantoLog.get('/order', { params: { ...baseParams, page: 1 } })
      const data1 = first.data || {}
      const totalPages = data1.totalPages || data1.totalPage || 1
      let allRaw = data1.orders || data1.data || (Array.isArray(data1) ? data1 : [])

      if (totalPages > 1) {
        const reqs = []
        for (let p = 2; p <= totalPages; p++) {
          reqs.push(apiSantoLog.get('/order', { params: { ...baseParams, page: p } }))
        }
        const rest = await Promise.all(reqs)
        for (const r of rest) {
          const list = r.data?.orders || r.data?.data || (Array.isArray(r.data) ? r.data : [])
          allRaw = allRaw.concat(list)
        }
      }

      setOrdersRaw(allRaw)
      setDeliveries(allRaw.map(apiOrderToPlanilha))

      // Hidrata bippedVolumes e lastBoxByOrder a partir dos volumes.
      // "Bipado" agora considera tanto first_scanned_at quanto coletado/embarcado,
      // pra refletir que o volume já chegou no galpão (mesmo antes da separação).
      const bipMap = {}
      const lastBoxMap = {}
      for (const o of allRaw) {
        const nf = String(o.order_number || '').trim()
        if (!nf) continue
        const set = new Set()
        let lastBox = null
        for (const v of (o.volumes || [])) {
          if (volumeFirstBipado(v)) {
            const n = volumeNumber(v)
            if (n != null) set.add(n)
          }
          // lastBox segue dependendo do "está fisicamente no box" — não de first-bipe
          if (volumeInBox(v)) {
            const bn = volumeBoxName(v)
            if (bn) lastBox = bn
          }
        }
        if (set.size) bipMap[nf] = set
        if (lastBox && o.id) lastBoxMap[o.id] = lastBox
      }
      setBippedVolumes(bipMap)
      setLastBoxByOrder(lastBoxMap)
      showToast(`✓ ${allRaw.length} pedido${allRaw.length !== 1 ? 's' : ''} carregado${allRaw.length !== 1 ? 's' : ''}`)
    } catch (err) {
      console.error('GET /order failed:', err)
      showToast('Erro ao carregar pedidos: ' + (err?.response?.data?.error || err.message), true)
    } finally {
      setLoadingOrders(false)
    }
  }, [showToast])

  /** Limpa estado local (não apaga nada do backend). Re-fetch acontece em paralelo. */
  const resetAllData = useCallback(() => {
    setDeliveries([])
    setBippedVolumes({})
    setLastBoxByOrder({})
    setBigResult(null)
    setConsultaResult(null)
  }, [])

  // ─── SCAN / NF SEARCH ──────────────────────────────────────────────────────
  /**
   * Busca a entrega pelo número da NF e atualiza os painéis de resultado.
   * Usado apenas pela consulta manual por NF e duplo-clique na tabela —
   * NÃO dispara modal de box (esse é responsabilidade do processRastreioScan).
   */
  const searchDeliveryByNf = useCallback(async (nf) => {
    const nfString = String(nf).trim()
    const rowIndex = deliveries.findIndex(row => String(row['Número'] || '').trim() === nfString)
    const delivery = rowIndex >= 0 ? deliveries[rowIndex] : null
    const lineNumber = rowIndex >= 0 ? rowIndex + 2 : null

    if (!delivery) {
      setConsultaResult({ type: 'notFound', nf: nfString })
      setBigResult(null)
      try {
        const res = await apiSantoLog.get(`/order/by-number/${nfString}`, { params: { module: 'conferencia' } })
        const order = res.data?.order || res.data || null
        if (order?.status === 'embarcado') {
          showToast(`Pedido ${nfString} já foi embarcado — use Re-embarcar`, true)
        }
      } catch { /* 404 esperado */ }
      return
    }
    setConsultaResult({ type: 'found' })
    setBigResult({ delivery, lineNumber, volumeBipped: null })
  }, [deliveries, showToast])

  // ─── CONSULTA QR — só lookup, sem persistência ───────────────────────────
  /**
   * Bipa o rastreio na aba Consulta QR. Mostra info do pedido (BigResult).
   * Nada é persistido — a marcação real acontece na aba Conferência.
   */
  const processRastreioScan = useCallback(async (rastreio) => {
    // Normaliza qualquer prefixo (SLEXPRESSO, TMRAPIDO, etc) para ALEF{NF}V{VOL}
    const r = normalizeRastreio(rastreio) || ''
    if (!r) return
    setScanChips({
      rawCode: r,
      nf: extractNfFromBarcode(r),
      volume: extractVolumeFromBarcode(r),
    })
    try {
      const res = await apiSantoLog.get(`/volume/by-rastreio/${encodeURIComponent(r)}`, { params: { module: 'conferencia' } })
      const raw = res.data?.data ?? res.data ?? {}
      const volume = raw.volume || (raw.rastreio ? raw : null)
      const order = raw.order || volume?.order || null
      if (!volume || !order) {
        playErrorBeep()
        setConsultaResult({ type: 'notFound', nf: r })
        setBigResult(null)
        showToast(`Rastreio ${r} não encontrado`, true)
        return
      }

      // ── First-bipe silencioso ───────────────────────────────────────────
      // Registra a chegada do volume no galpão sem feedback visual.
      // Cache em memória pra evitar bater na API se o operador rebipar o
      // mesmo rastreio na mesma sessão. Erros são ignorados silenciosamente.
      // Ao receber sucesso, atualiza ordersRaw e bippedVolumes pra refletir
      // o volume bipado IMEDIATAMENTE na Big Card (BIPADOS X/Y).
      if (!firstBipeCacheRef.current.has(r)) {
        firstBipeCacheRef.current.add(r)
        apiSantoLog
          .post('/volume/first-bipe', { rastreio: r })
          .then(resp => {
            const payload = resp?.data?.data ?? resp?.data ?? {}
            const vol = payload.volume || null
            if (!vol || !vol.id) return
            const orderId = vol.order?.id || vol.order_id || order.id
            const firstScannedAt = vol.first_scanned_at || new Date().toISOString()
            const volNum = volumeNumber(vol) ?? extractVolumeFromBarcode(vol.rastreio || r)
            // 1) Atualiza o volume dentro de ordersRaw — match por volume.id
            setOrdersRaw(prev => prev.map(o2 => {
              if (o2.id !== orderId) return o2
              const volumes = (o2.volumes || []).map(v2 =>
                v2.id === vol.id ? { ...v2, first_scanned_at: firstScannedAt } : v2
              )
              return { ...o2, volumes }
            }))
            // 2) Adiciona o número do volume no Set de bipados pra Big Card refletir
            const nfKey = String(order.order_number || '').trim()
            if (nfKey && volNum != null) {
              setBippedVolumes(prev => {
                const existing = prev[nfKey] || new Set()
                if (existing.has(volNum)) return prev
                const next = new Set(existing)
                next.add(volNum)
                return { ...prev, [nfKey]: next }
              })
            }
          })
          .catch(err => {
            // Falhou: remove do cache pra permitir retry no próximo bipe
            firstBipeCacheRef.current.delete(r)
            console.warn('[first-bipe] silent fail:', err?.response?.status, err?.message)
          })
      }

      // Mescla com ordersRaw se já carregado (pra ter todos os campos)
      const existingRaw = ordersRaw.find(x => x.id === order.id)
      const mergedOrder = existingRaw
        ? { ...existingRaw, ...Object.fromEntries(
            Object.entries(order).filter(([, v]) => v !== null && v !== undefined && v !== '')
          ) }
        : order

      const volNumFromCode = extractVolumeFromBarcode(volume.rastreio || r)
      const volNumber = volumeNumber(volume) ?? volNumFromCode

      const planilha = apiOrderToPlanilha(mergedOrder)
      playSuccessBeep()
      setConsultaResult({ type: 'found' })
      setBigResult({ delivery: planilha, lineNumber: null, volumeBipped: volNumber })
    } catch (err) {
      console.error('GET /volume/by-rastreio failed:', err)
      playErrorBeep()
      const msg = err?.response?.data?.error || err?.response?.data?.message || err.message
      setConsultaResult({ type: 'notFound', nf: r })
      setBigResult(null)
      showToast(`Rastreio ${r}: ${msg}`, true)
    }
  }, [ordersRaw, showToast])

  // ─── CONFERÊNCIA — bipa box → bipa rastreios ─────────────────────────────
  /**
   * Passo 1: Operador bipa o barcode do box → resolve box + carrega lista de
   * volumes esperados via GET /box/:id/conference.
   */
  const loadConferenceBox = useCallback(async (barcode) => {
    setConfBusy(true)
    setConfFeedback({ kind: null, message: 'Buscando box…' })
    try {
      const boxRes = await apiSantoLog.get(`/box/by-barcode/${encodeURIComponent(barcode)}`, { params: { module: 'conferencia' } }).catch(err => {
        if (err?.response?.status === 404) return null
        throw err
      })
      if (!boxRes) {
        playErrorBeep()
        setConfFeedback({ kind: 'err', message: `Box "${barcode}" não encontrado` })
        setConfModal({
          open: true, kind: 'error',
          title: 'Box não encontrado',
          message: `Nenhum box com código "${barcode}" foi localizado.`,
        })
        return
      }
      const boxBase = boxRes.data?.box || boxRes.data || {}

      const confRes = await apiSantoLog.get(`/box/${boxBase.id}/conference`)
      const data = confRes.data || {}
      const box = data.box || boxBase
      const driver = data.driver || box.driver || null
      const volumes = data.volumes || []
      setConfBox({
        id: box.id,
        name: box.name,
        barcode: box.barcode,
        driver_id: driver?.id || null,
        driver_name: driver?.name || null,
        total: data.total || volumes.length,
      })
      setConfVolumes(volumes)
      const total = data.total || volumes.length
      playSuccessBeep()
      setConfFeedback({
        kind: 'ok',
        message: total > 0
          ? `📦 ${box.name} · ${total} volume${total !== 1 ? 's' : ''} pra conferir${driver?.name ? ` · ${driver.name}` : ''}`
          : `📦 ${box.name} está sem volumes pendentes`,
      })
      setTimeout(armScanner, 60)
    } catch (err) {
      console.error('GET /box/:id/conference failed:', err)
      const status = err?.response?.status
      const msg = err?.response?.data?.error || err?.response?.data?.message || err.message
      // 422 → geralmente "box sem motorista vinculado" — é warning, não erro
      if (status === 422) {
        playWarningBeep()
        setConfModal({ open: true, kind: 'warning', title: 'Atenção', message: msg })
      } else {
        playErrorBeep()
        setConfModal({ open: true, kind: 'error', title: 'Erro', message: msg })
      }
      setConfFeedback({ kind: 'err', message: msg })
    } finally {
      setConfBusy(false)
    }
  }, [])

  /**
   * Passo 2: Operador bipa cada volume → POST /conference/scan { box_id, rastreio }.
   * Em 422 com correct_box, abre modal de alerta com som + opção de ir pro box certo.
   */
  const scanConferenceVolume = useCallback(async (rastreio) => {
    if (!confBox) return
    setConfBusy(true)
    setConfFeedback({ kind: null, message: 'Conferindo…' })
    try {
      const res = await apiSantoLog.post('/conference/scan', {
        box_id: confBox.id,
        rastreio,
      })
      const data = res.data || {}
      const volume = data.volume || {}
      const orderStatus = data.order_status
      if (data.already_coletado) {
        // NÃO abre modal — apenas feedback inline + beep de aviso.
        // O operador continua bipando sem interrupção.
        playWarningBeep()
        setConfFeedback({ kind: 'warn', message: `⚠ ${rastreio} já estava conferido — siga bipando` })
      } else {
        playSuccessBeep()
        setConfFeedback({
          kind: 'ok',
          message: `✓ ${rastreio} conferido${orderStatus === 'coletado' ? ' · pedido completo!' : ''}`,
        })
      }
      // Marca o volume como coletado na lista local + detecta finalização
      setConfVolumes(prev => {
        const next = prev.map(v => {
          const matches = v.rastreio === rastreio || v.id === volume.id
          return matches ? { ...v, status: 'coletado' } : v
        })
        // Conferência 100% finalizada? abre modal de sucesso → ao fechar reseta o box
        const allDone = next.length > 0 && next.every(v => v.status === 'coletado' || v.status === 'embarcado')
        if (allDone) {
          // setTimeout pra garantir que o setState atual já flushou antes do modal
          setTimeout(() => {
            playSuccessBeep()
            setConfModal({
              open: true, kind: 'success',
              title: '🎉 Conferência finalizada',
              message: `Todos os ${next.length} volume${next.length !== 1 ? 's' : ''} de ${confBox?.name || 'este box'} foram conferidos. Bipe um novo box para continuar.`,
              finalize: true,
            })
          }, 100)
        }
        return next
      })
      setTimeout(armScanner, 60)
    } catch (err) {
      console.error('POST /conference/scan failed:', err)
      const raw = err?.response?.data || {}
      const data = raw.data || raw
      const correctBox = data.correct_box || data.actual_box || raw.correct_box || raw.actual_box || null
      const order = data.order || raw.order || null
      const errMsg = data.error || raw.error || data.message || raw.message || err.message
      const status = err?.response?.status

      if (status === 404) {
        // Rastreio/Box não encontrado → ERROR
        playErrorBeep()
        setConfModal({
          open: true, kind: 'error',
          title: 'Não encontrado',
          message: errMsg || 'Rastreio ou box não localizado',
        })
        setConfFeedback({ kind: 'err', message: errMsg })
      } else if (status === 422) {
        // 422 → regra de negócio (volume errado / sem motorista) → WARNING
        playWarningBeep()
        setConfModal({
          open: true, kind: 'warning',
          title: 'Volume não pertence a este box',
          message: errMsg || 'Volume não pode ser conferido neste box',
          order, correct_box: correctBox,
        })
        setConfFeedback({ kind: 'err', message: errMsg || 'Volume errado' })
      } else {
        // Outros erros (500, network, etc) → ERROR
        playErrorBeep()
        setConfModal({
          open: true, kind: 'error',
          title: 'Erro',
          message: 'Erro: ' + errMsg,
        })
        setConfFeedback({ kind: 'err', message: 'Erro: ' + errMsg })
      }
    } finally {
      setConfBusy(false)
    }
  }, [confBox])

  function resetConferenceBox() {
    setConfBox(null)
    setConfVolumes([])
    setConfScanValue('')
    setConfFeedback({ kind: null, message: '' })
    setConfModal(initialConfModal)
  }

  function closeConfModal() {
    const wasFinalize = confModal.finalize
    setConfModal(initialConfModal)
    if (wasFinalize) {
      // Conferência terminada: limpa o box e volta pro passo "bipar box"
      resetConferenceBox()
    } else {
      // Re-foca o input imediatamente pra o operador continuar bipando
      setTimeout(armScanner, 30)
    }
  }

  // Ref pra evitar closure stale no onBlur — foco perpétuo precisa saber
  // em tempo real se o modal está aberto.
  const confModalOpenRef = useRef(false)
  useEffect(() => { confModalOpenRef.current = confModal.open }, [confModal.open])

  // Handler do blur — re-foca o input em 50ms a menos que o foco tenha ido pra
  // um elemento interativo legítimo (botão do modal etc).
  const handleConfBlur = useCallback(() => {
    setTimeout(() => {
      if (confModalOpenRef.current) return
      const ae = document.activeElement
      if (ae && ae !== document.body && ae !== confScanInputRef.current) {
        const tag = ae.tagName
        if (tag === 'BUTTON' || tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || ae.isContentEditable) return
      }
      armScanner()
    }, 50)
  }, [armScanner])

  // Dispatcher do input da aba Conferência: decide se é box ou rastreio.
  // Box: código bipado direto (barcode do box). Rastreio: normaliza pra ALEF{NF}V{VOL}.
  function onConferenceScan(code) {
    if (!code) return
    if (!confBox) loadConferenceBox(code)
    else scanConferenceVolume(normalizeRastreio(code))
  }

  // ─── TABLE SORT ─────────────────────────────────────────────────────────────
  /** Altera a coluna de ordenação; inverte direção se a coluna já estava selecionada */
  const handleColumnSort = useCallback((columnKey) => {
    setSortColumn(prev => {
      if (prev === columnKey) {
        setSortAscending(asc => !asc)
        return prev
      }
      setSortAscending(true)
      return columnKey
    })
  }, [])

  // ─── DERIVED DATA (useMemo para evitar recálculo desnecessário) ─────────────

  /** Opções únicas de filtro extraídas dos dados carregados */
  const filterOptions = useMemo(() => {
    const situacoes = [...new Set(deliveries.map(r => r['Situação']).filter(Boolean))].sort()
    const motoristas = [...new Set(deliveries.map(r => r['Motorista']).filter(Boolean))].sort()
    const rotas = [...new Set(deliveries.map(r => r['Rota']).filter(Boolean))].sort()
    return { situacoes, motoristas, rotas }
  }, [deliveries])

  /** Entregas filtradas e ordenadas conforme os controles da tabela */
  const filteredDeliveries = useMemo(() => {
    const query = debouncedSearch.toLowerCase()

    let rows = deliveries.filter(row => {
      if (filterSituacao && row['Situação'] !== filterSituacao) return false
      if (filterMotorista && row['Motorista'] !== filterMotorista) return false
      if (filterRota && row['Rota'] !== filterRota) return false
      if (query) {
        const haystack = [row['Número'], row['Motorista'], row['Destinatário'], row['Bairro'], row['Rota'], row['Situação']]
          .map(v => String(v || '').toLowerCase())
          .join(' ')
        if (!haystack.includes(query)) return false
      }
      return true
    })

    rows.sort((a, b) => {
      let valA = a[sortColumn] || ''
      let valB = b[sortColumn] || ''
      if (!isNaN(valA) && !isNaN(valB)) { valA = +valA; valB = +valB }
      else { valA = String(valA).toLowerCase(); valB = String(valB).toLowerCase() }
      return sortAscending ? (valA > valB ? 1 : -1) : (valA < valB ? 1 : -1)
    })

    return rows
  }, [deliveries, debouncedSearch, filterSituacao, filterMotorista, filterRota, sortColumn, sortAscending])

  /** Estatísticas de importação para o painel de resumo */
  const importStats = useMemo(() => {
    if (!deliveries.length) return null
    return {
      total: deliveries.length,
      aguardando: deliveries.filter(r => (r['Situação'] || '').includes('Aguardando')).length,
      aceita: deliveries.filter(r => (r['Situação'] || '').includes('Aceita')).length,
      recep: deliveries.filter(r => (r['Situação'] || '').includes('Recepcionada')).length,
      motoristas: new Set(deliveries.map(r => r['Motorista']).filter(Boolean)).size,
      pesoTotal: deliveries.reduce((sum, r) => sum + (parseFloat(r['Peso (KG)']) || 0), 0),
    }
  }, [deliveries])

  /** Estatísticas da aba de entregas incluindo volumes bipados */
  const entregasStats = useMemo(() => {
    if (!deliveries.length) return null
    const totalVolumes = deliveries.reduce((sum, r) => sum + (parseInt(r['Quantidade de Volumes']) || 0), 0)
    const totalBipados = Object.values(bippedVolumes).reduce((sum, set) => sum + set.size, 0)
    const recep = deliveries.filter(r => (r['Situação'] || '').includes('Recepcionada')).length
    const pesoTotal = deliveries.reduce((sum, r) => sum + (parseFloat(r['Peso (KG)']) || 0), 0)
    return { total: deliveries.length, totalVolumes, totalBipados, recep, pesoTotal }
  }, [deliveries, bippedVolumes])

  /** Agrupamento de entregas por motorista para a aba de motoristas */
  const driverGroups = useMemo(() => {
    const map = {}
    deliveries.forEach(row => {
      const driver = row['Motorista']
      if (!driver) return
      if (!map[driver]) map[driver] = { deliveries: [], routes: new Set() }
      map[driver].deliveries.push(row)
      if (row['Rota']) map[driver].routes.add(row['Rota'])
    })
    return Object.entries(map).sort((a, b) => b[1].deliveries.length - a[1].deliveries.length)
  }, [deliveries])

  /** Entregas sem motorista atribuído */
  const unassignedDeliveries = useMemo(
    () => deliveries.filter(r => !r['Motorista']),
    [deliveries],
  )

  // ─── RENDER HELPERS ────────────────────────────────────────────────────────

  /**
   * Renderiza o painel principal com os dados da entrega encontrada.
   * Layout em 3 blocos:
   *  1. Header: Motorista (esquerda) + Sequência (direita)
   *  2. Grid de campos: Nota Fiscal | Bipados, Destinatário (full), Bairro | CEP
   *  3. Rodapé de métricas: Volumes | Peso
   */
  function renderBigResultPanel(data) {
    const { delivery, volumeBipped } = data
    const nfString = String(delivery['Número'] || '').trim()
    const bippedSet = bippedVolumes[nfString] || new Set()
    const bippedCount = bippedSet.size
    const totalVolumes = parseInt(delivery['Quantidade de Volumes']) || 1
    const allVolumesBipped = bippedCount >= totalVolumes

    // Cor da contagem de bipados conforme progresso
    const bipColor = allVolumesBipped ? '#00e5a0' : bippedCount > 0 ? '#f59e0b' : '#e8ecf0'

    const driverName = delivery['Motorista'] ? extractDriverName(delivery['Motorista']) : null
    const recipientName = cleanRecipientName(delivery['Destinatário'] || '—')
    const formattedWeight = delivery['Peso (KG)']
      ? parseFloat(delivery['Peso (KG)']).toFixed(3) + ' kg'
      : '—'
    const isLongRecipient = recipientName.length > 20

    return (
      <BigResult>
        {/* ── Header: Motorista (destaque) + Sequência ───────────────────── */}
        <BigResultHeader>
          <div>
            <BigFieldLabel>Motorista</BigFieldLabel>
            <BigFieldVal $sm $variant={driverName ? 'accent' : 'danger'}>
              {driverName || 'SEM MOTORISTA'}
            </BigFieldVal>
          </div>
          <div style={{ textAlign: 'right' }}>
            <BigNfLabel>Sequência</BigNfLabel>
            <BigSeqVal>{delivery['Sequência'] || '—'}</BigSeqVal>
          </div>
        </BigResultHeader>

        {/* ── Grid de campos principais ──────────────────────────────────── */}
        <BigFieldsGrid>
          {/* Linha 1, Coluna 1: Nota Fiscal */}
          <BigField>
            <BigFieldLabel>Nota Fiscal</BigFieldLabel>
            <BigFieldVal>{delivery['Número']}</BigFieldVal>
          </BigField>

          {/* Linha 1, Coluna 2: Métrica de Bipados (com badge se houver volume novo) */}
          <BigMetric>
            <BigMetricLabel>Bipados</BigMetricLabel>
            <BigMetricVal $color={bipColor}>{bippedCount}/{totalVolumes}</BigMetricVal>
            {volumeBipped !== null && volumeBipped !== undefined && (
              <BipBadge>✓ VOLUME BIPADO · V{volumeBipped}</BipBadge>
            )}
          </BigMetric>

          {/* Linha 2: Destinatário (ocupa as duas colunas) */}
          <BigField $full $noRight>
            <BigFieldLabel>Destinatário</BigFieldLabel>
            <BigFieldVal $sm={isLongRecipient}>{recipientName}</BigFieldVal>
          </BigField>

          {/* Linha 3, Coluna 1: Bairro */}
          <BigField $noRight $noBorderBottom>
            <BigFieldLabel>Bairro</BigFieldLabel>
            <BigFieldVal $sm>{delivery['Bairro'] || '—'}</BigFieldVal>
          </BigField>

          {/* Linha 3, Coluna 2: CEP */}
          <BigField $noRight $noBorderBottom>
            <BigFieldLabel>CEP</BigFieldLabel>
            <BigFieldVal $sm $mono>{formatCep(delivery['CEP'])}</BigFieldVal>
          </BigField>
        </BigFieldsGrid>

        {/* ── Rodapé com métricas de Volumes e Peso ─────────────────────── */}
        <BigMetricsRow>
          <BigMetric $noBorderBottom>
            <BigMetricLabel>Volumes</BigMetricLabel>
            <BigMetricVal>{totalVolumes}</BigMetricVal>
          </BigMetric>
          <BigMetric $noRight $noBorderBottom>
            <BigMetricLabel>Peso</BigMetricLabel>
            <BigMetricVal>{formattedWeight}</BigMetricVal>
          </BigMetric>
        </BigMetricsRow>
      </BigResult>
    )
  }

  /**
   * Renderiza o painel de detalhes completos de uma entrega.
   * `lineNumber` é a linha correspondente na planilha original (1-based, com header).
   */
  function renderDetailPanel(delivery, lineNumber) {
    const nfString = String(delivery['Número'] || '').trim()
    const bippedSet = bippedVolumes[nfString] || new Set()
    const totalVolumes = parseInt(delivery['Quantidade de Volumes']) || 1
    const bipVariant = bippedSet.size >= totalVolumes ? 'accent' : bippedSet.size > 0 ? 'warn' : null
    const driverName = delivery['Motorista'] ? extractDriverName(delivery['Motorista']) : null
    const situacao = delivery['Situação'] || ''
    const situacaoVariant = situacao.includes('Aguardando')
      ? 'warn'
      : situacao.includes('Aceita') ? 'accent' : null

    // Janela de previsão (início → fim) formatada
    const previsaoLabel = `${formatDate(delivery['Previsão Início - Data / Hora'])} → ${formatDate(delivery['Previsão Fim - Data / Hora'])}`

    const rows = [
      { key: 'Situação', value: situacao || '—', variant: situacaoVariant },
      { key: 'Classificação', value: delivery['Classificação'] || '—' },
      { key: 'Motorista', value: driverName || 'Não atribuído', variant: driverName ? null : 'danger' },
      { key: 'Rota', value: delivery['Rota'] || '—' },
      { key: 'Sequência', value: delivery['Sequência'] || '—' },
      { key: 'Linha da Planilha', value: lineNumber ?? '—', mono: true },
      { key: 'Destinatário', value: cleanRecipientName(delivery['Destinatário'] || '—') },
      { key: 'E-mail', value: delivery['E-mail'] || '—' },
      { key: 'Logradouro', value: [delivery['Logradouro'], delivery['Complemento']].filter(Boolean).join(', ') || '—' },
      { key: 'Bairro', value: delivery['Bairro'] || '—' },
      { key: 'Cidade / UF', value: [delivery['Cidade'], delivery['UF']].filter(Boolean).join(' / ') || '—' },
      { key: 'CEP', value: formatCep(delivery['CEP']), mono: true },
      { key: 'Peso', value: delivery['Peso (KG)'] ? parseFloat(delivery['Peso (KG)']).toFixed(3) + ' kg' : '—' },
      { key: 'Volumes Total', value: String(totalVolumes) },
      { key: 'Volumes Bipados', value: `${bippedSet.size}/${totalVolumes}`, variant: bipVariant },
      { key: 'Observação', value: delivery['Observação'] || '—' },
      { key: 'Previsão', value: previsaoLabel },
      { key: 'Tags', value: delivery['Tags'] || '—' },
      { key: 'Recebedor', value: delivery['Recebedor'] || '—' },
    ]

    return (
      <DetailSection>
        <DetailHeader>
          <h3>Detalhes Completos · NF {delivery['Número']}</h3>
        </DetailHeader>
        {rows.map(({ key, value, variant, mono }) => (
          <DetailRow key={key}>
            <DetailKey>{key}</DetailKey>
            <DetailVal $variant={variant} $mono={mono}>{value}</DetailVal>
          </DetailRow>
        ))}
      </DetailSection>
    )
  }

  // ─── RENDER: UPLOAD TAB ────────────────────────────────────────────────────
  // (renderUploadTab foi removido — importação migrou para /santolog/pedidos.
  //  Mantemos resetAllData só pra limpar estado local de bipes na sessão atual.)
  function goToPedidos() { history.push('/santolog/pedidos') }

  // ─── RENDER: CONSULTA TAB ──────────────────────────────────────────────────
  function renderConsultaTab() {
    if (!deliveries.length) {
      return (
        <NoDataScreen>
          <div className="icon">🔍</div>
          <p>Importe uma planilha primeiro</p>
          <GoUploadBtn onClick={goToPedidos}>Ir para Cadastros · Pedidos</GoUploadBtn>
        </NoDataScreen>
      )
    }

    // Entrega encontrada (delivery + linha na planilha) para o painel de detalhes
    const foundDelivery = bigResult?.delivery || null
    const foundLineNumber = bigResult?.lineNumber || null

    return (
      <ConsultaGrid>
        {/* Coluna esquerda: campos de busca */}
        <ConsultaCol>
          <Card>
            <CardHeader>
              <h3>Consulta por QR / Barcode</h3>
              <CardBadge>BIPE</CardBadge>
            </CardHeader>
            <CardBody>
              <ScanLabel htmlFor="scan-input">Rastreio / código da etiqueta</ScanLabel>
              <ScanInputWrap>
                <ScanInput
                  id="scan-input"
                  ref={scanInputRef}
                  value={scanValue}
                  onChange={e => setScanValue(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      const code = scanValue.trim()
                      setScanValue('')
                      if (code) processRastreioScan(code)
                    }
                  }}
                  placeholder="ex: ALEF642141V1"
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck={false}
                  style={isCoarse ? HIDDEN_INPUT_STYLE : undefined}
                />
                {!isCoarse && <ScanIcon>⌨</ScanIcon>}
              </ScanInputWrap>

              {scanChips && (
                <NfChipsRow>
                  <NfChip>Código: {scanChips.rawCode}</NfChip>
                  <NfChip $highlight>
                    NF: {scanChips.nf || '—'}{scanChips.volume !== null ? ` · V${scanChips.volume}` : ''}
                  </NfChip>
                </NfChipsRow>
              )}

              {consultaResult?.type === 'notFound' && (
                <NotFoundBox>
                  Código <strong>{consultaResult.nf}</strong> não encontrado na base
                </NotFoundBox>
              )}
            </CardBody>
          </Card>

          <Card>
            <CardHeader>
              <h3>Consulta por Número NF</h3>
            </CardHeader>
            <CardBody>
              <ScanLabel htmlFor="nf-input">Número da Nota Fiscal</ScanLabel>
              <ScanInputWrap>
                <ScanInput
                  id="nf-input"
                  type="number"
                  value={nfValue}
                  onChange={e => {
                    setNfValue(e.target.value)
                    if (e.target.value.trim()) searchDeliveryByNf(e.target.value.trim())
                    else { setConsultaResult(null); setBigResult(null) }
                  }}
                  placeholder="ex: 642141"
                  autoComplete="off"
                />
                <ScanIcon>#</ScanIcon>
              </ScanInputWrap>
            </CardBody>
          </Card>
        </ConsultaCol>

        {/* Coluna direita: resultado */}
        <ConsultaCol>
          {bigResult
            ? renderBigResultPanel(bigResult)
            : (
              <EmptyPlaceholder>
                <div className="icon">📦</div>
                <p>Bipe ou digite o código para consultar</p>
              </EmptyPlaceholder>
            )
          }

          {foundDelivery && renderDetailPanel(foundDelivery, foundLineNumber)}
        </ConsultaCol>
      </ConsultaGrid>
    )
  }

  // ─── RENDER: CONFERÊNCIA TAB ──────────────────────────────────────────────
  /**
   * Fluxo box-first:
   *   1) Operador bipa o box → GET /box/by-barcode → GET /box/:id/conference
   *   2) Mostra lista de volumes esperados (rastreio + NF + motorista)
   *   3) Operador bipa cada volume → POST /conference/scan
   *      - sucesso: marca como coletado
   *      - 422 com correct_box: modal de alerta + som de erro
   */
  function renderConferenceTab() {
    const coletadoCount = confVolumes.filter(v => v.status === 'coletado' || v.status === 'embarcado').length
    const totalCount = confVolumes.length
    const allDone = totalCount > 0 && coletadoCount >= totalCount

    return (
      <ConfWrap>
        <ConfScanCard>
          <div className="icon">{!confBox ? '📦' : '🔍'}</div>
          <h3>{!confBox ? 'Bipar box para iniciar conferência' : 'Bipar volume para conferir'}</h3>
          <p>
            {!confBox
              ? <>Comece bipando o <strong style={{ color: '#00e5a0' }}>código do box</strong>. Vamos listar os volumes esperados.</>
              : <>Bipe cada rastreio em <strong style={{ color: '#00e5a0' }}>{confBox.name}</strong>{confBox.driver_name ? <> (motorista <strong>{confBox.driver_name}</strong>)</> : null}.</>}
          </p>
          <ConfScanInput
            ref={confScanInputRef}
            value={confScanValue}
            $kind={confFeedback.kind}
            onChange={e => setConfScanValue(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault()
                const code = confScanValue.trim()
                setConfScanValue('')
                onConferenceScan(code)
              }
            }}
            onBlur={handleConfBlur}
            placeholder={!confBox ? 'Bipe o código do box' : 'Bipe o rastreio do volume'}
            disabled={confBusy}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            style={isCoarse ? HIDDEN_INPUT_STYLE : undefined}
          />
          <ConfFeedback $kind={confFeedback.kind}>
            {confFeedback.message || (confBusy ? 'Processando…' : 'Aguardando bipe…')}
          </ConfFeedback>
        </ConfScanCard>

        {confBox && (
          <ConfBoxBanner $complete={allDone}>
            <div className="info">
              <div className="lbl">Box atual</div>
              <div className="name">📦 {confBox.name}</div>
              <div className="meta">
                {confBox.barcode && <>código <strong>{confBox.barcode}</strong></>}
                {confBox.driver_name && <> · motorista <strong>{confBox.driver_name}</strong></>}
              </div>
            </div>
            <div>
              <div className="progress">{coletadoCount}/{totalCount}</div>
              <div className="progress-lbl">Conferidos</div>
            </div>
          </ConfBoxBanner>
        )}

        {confBox && totalCount > 0 && (
          <ConfPanel>
            <h4>Volumes esperados ({coletadoCount}/{totalCount})</h4>
            <ConfVolumesList>
              {confVolumes.map(v => {
                const isColetado = v.status === 'coletado'
                const isEmbarked = v.status === 'embarcado'
                const order = v.order || {}
                return (
                  <ConfVolumeRow key={v.id || v.rastreio} $coletado={isColetado} $embarked={isEmbarked}>
                    <span className="check">
                      {isEmbarked ? '🚛' : isColetado ? '✓' : '○'}
                    </span>
                    <div className="body">
                      <div className="rastreio">{v.rastreio || '—'}</div>
                      <div className="meta">
                        NF {order.order_number || '—'}
                        {order.recipient_name && ` · ${cleanRecipientName(order.recipient_name)}`}
                        {order.sequence_order != null && ` · seq ${order.sequence_order}`}
                        {order.driver_name && ` · 🚛 ${extractDriverName(order.driver_name)}`}
                        {confBox?.name && ` · 📦 ${confBox.name}`}
                        {v.first_scanned_at && ` · 🔵 1º beep ${formatBeep(v.first_scanned_at)}`}
                      </div>
                    </div>
                    <span className="nf">V{v.volume || v.volume_number || '?'}</span>
                  </ConfVolumeRow>
                )
              })}
            </ConfVolumesList>
          </ConfPanel>
        )}

        {confBox && totalCount === 0 && (
          <ConfPanel>
            <h4>📦 Box sem volumes pendentes</h4>
            <div style={{ fontSize: 12, color: '#8a95a3', textAlign: 'center', padding: 12 }}>
              Nenhum volume aguardando conferência neste box.
            </div>
          </ConfPanel>
        )}

        {confBox && (
          <ConfResetBtn onClick={resetConferenceBox} disabled={confBusy}>
            ✕ Trocar de box
          </ConfResetBtn>
        )}
      </ConfWrap>
    )
  }

  // ─── RENDER: ENTREGAS TAB ─────────────────────────────────────────────────
  function renderEntregasTab() {
    if (!ordersRaw.length) {
      return (
        <NoDataScreen>
          <div className="icon">📦</div>
          <p>Importe uma planilha primeiro</p>
          <GoUploadBtn onClick={goToPedidos}>Ir para Cadastros · Pedidos</GoUploadBtn>
        </NoDataScreen>
      )
    }

    const counts = {
      all: ordersRaw.length,
      aguardando: ordersRaw.filter(o => o.status === 'aguardando_recebimento').length,
      parcial: ordersRaw.filter(o => o.status === 'coletado_parcial').length,
      coletado: ordersRaw.filter(o => o.status === 'coletado').length,
      embarcado: ordersRaw.filter(o => o.status === 'embarcado').length,
      entregue: ordersRaw.filter(o => o.status === 'entregue').length,
      nao_embarcado: ordersRaw.filter(o => o.status === 'nao_embarcado').length,
    }

    const matches = (o, term) => {
      if (!term) return true
      const t = term.toLowerCase()
      const haystack = [o.order_number, o.recipient_name, o.driver_name, o.neighborhood, o.city, o.route]
        .map(v => String(v || '').toLowerCase()).join(' ')
      return haystack.includes(t)
    }

    const filterMap = {
      parcial: 'coletado_parcial',
      aguardando: 'aguardando_recebimento',
      coletado: 'coletado',
      embarcado: 'embarcado',
      entregue: 'entregue',
      nao_embarcado: 'nao_embarcado',
    }
    let list = ordersRaw
    if (filterMap[entregasFilter]) {
      list = list.filter(o => o.status === filterMap[entregasFilter])
    }
    list = list.filter(o => matches(o, tableSearch))

    return (
      <>
        <EntregasToolbar>
          <SearchInput
            value={tableSearch}
            onChange={e => setTableSearch(e.target.value)}
            placeholder="🔍  Buscar por NF, destinatário, bairro, motorista..."
            style={{ maxWidth: 360 }}
          />
          <StatusChip $active={entregasFilter === 'all'} onClick={() => setEntregasFilter('all')}>
            Todos <span className="count">{counts.all}</span>
          </StatusChip>
          <StatusChip $active={entregasFilter === 'aguardando'} onClick={() => setEntregasFilter('aguardando')}>
            Aguardando <span className="count">{counts.aguardando}</span>
          </StatusChip>
          <StatusChip $active={entregasFilter === 'parcial'} onClick={() => setEntregasFilter('parcial')}>
            Parcial <span className="count">{counts.parcial}</span>
          </StatusChip>
          <StatusChip $active={entregasFilter === 'coletado'} onClick={() => setEntregasFilter('coletado')}>
            Coletado <span className="count">{counts.coletado}</span>
          </StatusChip>
          <StatusChip $active={entregasFilter === 'embarcado'} onClick={() => setEntregasFilter('embarcado')}>
            Embarcado <span className="count">{counts.embarcado}</span>
          </StatusChip>
          <StatusChip $active={entregasFilter === 'entregue'} onClick={() => setEntregasFilter('entregue')}>
            Entregue <span className="count">{counts.entregue}</span>
          </StatusChip>
          <StatusChip $active={entregasFilter === 'nao_embarcado'} onClick={() => setEntregasFilter('nao_embarcado')}>
            Não Embarcado <span className="count">{counts.nao_embarcado}</span>
          </StatusChip>
        </EntregasToolbar>

        {list.length === 0 ? (
          <EmptyPlaceholder>
            <div className="icon">🔎</div>
            <p>Nenhum pedido corresponde aos filtros</p>
          </EmptyPlaceholder>
        ) : (
          <AccordionList>
            {list.map(o => {
              const total = Number(o.volumes_count) || (o.volumes?.length || 1)
              // "Scanned" pro accordion conta tudo que já passou por box (inclusive embarcado).
              // Pedido pode estar "coletado" com volumes embarcados.
              const scannedVols = (o.volumes || []).filter(volumeInBox)
              const scanned = scannedVols.length
              const expanded = expandedOrderId === o.id
              const complete = scanned >= total
              const partial  = scanned > 0 && scanned < total

              // Lista ordenada V1..Vn, mapeando se já está em box, embarcado, ou aguardando
              const byNumber = {}
              for (const v of (o.volumes || [])) {
                const n = volumeNumber(v)
                if (n != null) byNumber[n] = v
              }
              const volumeSlots = Array.from({ length: total }, (_, i) => {
                const num = i + 1
                const v = byNumber[num] || null
                return {
                  num,
                  rastreio: v?.rastreio || null,
                  box_name: volumeBoxNameFor(v, o.driver_boxes),
                  in_box: volumeActiveInBox(v),
                  embarked: volumeIsEmbarked(v),
                  first_scanned_at: v?.first_scanned_at || null,
                }
              })

              return (
                <AccordionItem key={o.id}>
                  <AccordionHeader
                    $expanded={expanded}
                    $complete={complete}
                    $partial={partial}
                    onClick={() => setExpandedOrderId(prev => prev === o.id ? null : o.id)}
                    type="button"
                  >
                    <span className="nf">
                      {o.sequence_order != null && (
                        <span
                          title={`Sequência de entrega #${o.sequence_order}`}
                          style={{
                            marginRight: 6,
                            display: 'inline-flex',
                            alignItems: 'center',
                            padding: '1px 5px',
                            borderRadius: 4,
                            fontSize: 10,
                            fontWeight: 700,
                            background: '#1e293b',
                            color: '#94a3b8',
                            border: '1px solid #334155',
                            fontFamily: "'Space Mono', monospace",
                            verticalAlign: 'middle',
                          }}
                        >
                          #{o.sequence_order}
                        </span>
                      )}
                      {o.order_number || '—'}
                      {o.has_reentrega && (
                        <span
                          title="Pedido importado em reentrega"
                          style={{
                            marginLeft: 6,
                            display: 'inline-flex',
                            alignItems: 'center',
                            padding: '1px 5px',
                            borderRadius: 4,
                            fontSize: 9,
                            fontWeight: 700,
                            letterSpacing: 0.3,
                            background: '#fee2e2',
                            color: '#b91c1c',
                            border: '1px solid #fca5a5',
                            verticalAlign: 'middle',
                          }}
                        >
                          ↩ REENTREGA
                        </span>
                      )}
                    </span>
                    <span className="recipient" title={o.recipient_name || ''}>
                      {cleanRecipientName(o.recipient_name || '—')}
                    </span>
                    <span className="driver">
                      🚛 {extractDriverName(o.driver_name || '') || 'SEM MOTORISTA'}
                      {o.route ? ` · ${o.route}` : ''}
                    </span>
                    <AccordionStatusBadge $status={o.status}>
                      {o.status === 'aguardando_recebimento' ? 'Ag. Receb.' :
                       o.status === 'coletado_parcial' ? 'Parcial' :
                       o.status === 'coletado' ? 'Coletado' :
                       o.status === 'embarcado' ? 'Embarcado' :
                       o.status === 'entregue' ? 'Entregue' :
                       o.status === 'nao_embarcado' ? 'Não Emb.' :
                       o.status}
                    </AccordionStatusBadge>
                    <span className="count">{scanned}/{total}</span>
                    <span className="caret">▾</span>
                  </AccordionHeader>

                  {expanded && (
                    <AccordionBody>
                      <VolumeGrid>
                        {volumeSlots.map(slot => (
                          <VolumeChip key={slot.num} $scanned={slot.in_box} $embarked={slot.embarked}>
                            <span className="num">V{slot.num}</span>
                            <div className="body">
                              <div className="rastreio">{slot.rastreio || '—'}</div>
                              <div className="box">
                                {slot.embarked
                                  ? `🚛 Embarcado${slot.box_name ? ` · saiu do ${slot.box_name}` : ''}`
                                  : slot.in_box
                                    ? `📦 ${slot.box_name || '(sem nome)'}`
                                    : 'Aguardando bipagem'}
                              </div>
                              {slot.first_scanned_at && (
                                <div className="box">🔵 1º beep {formatBeepShort(slot.first_scanned_at)}</div>
                              )}
                            </div>
                          </VolumeChip>
                        ))}
                      </VolumeGrid>
                    </AccordionBody>
                  )}
                </AccordionItem>
              )
            })}
          </AccordionList>
        )}
      </>
    )
  }

  // ─── RENDER: BOXES TAB ────────────────────────────────────────────────────
  /**
   * Aba Boxes — agora baseada em `driver_boxes[]` (vem em cada pedido).
   *
   * Regras de agrupamento (por pedido NÃO embarcado/entregue/nao_embarcado):
   *   - Tem motorista + `driver_boxes[]` populado → aparece em CADA box vinculado
   *   - Tem motorista, mas `driver_boxes` vazio        → card virtual "🆕 Definir BOX"
   *                                                       (1 por motorista)
   *   - Sem motorista                                  → card virtual "(Sem motorista)"
   *
   * Counts:
   *   - totalExpected = soma de volumes_count dos pedidos vinculados (todos os
   *                     pedidos não-terminais, não só os com volume coletado)
   *   - totalScanned  = volumes em status `coletado` que estão fisicamente
   *                     naquele box específico (via box_volume.box.id match)
   */
  function renderBoxesTab() {
    if (!ordersRaw.length) {
      return (
        <NoDataScreen>
          <div className="icon">🗃</div>
          <p>Importe uma planilha primeiro</p>
          <GoUploadBtn onClick={goToPedidos}>Ir para Cadastros · Pedidos</GoUploadBtn>
        </NoDataScreen>
      )
    }

    const boxesMap = {}
    // Status terminais que NÃO devem aparecer em box (saíram da operação):
    const TERMINAL = new Set(['embarcado', 'entregue', 'nao_embarcado'])

    function ensureBox(key, name, isVirtual) {
      if (!boxesMap[key]) {
        boxesMap[key] = {
          key, name, isVirtual: !!isVirtual,
          orders: [], rawOrders: [],
          totalScanned: 0, totalExpected: 0, weightKg: 0,
        }
      }
      return boxesMap[key]
    }

    for (const o of ordersRaw) {
      if (TERMINAL.has(o.status)) continue

      // 1) Determina onde o pedido vai aparecer (1 ou mais boxes)
      const driverBoxes = Array.isArray(o.driver_boxes) ? o.driver_boxes : []
      let targets = []
      if (driverBoxes.length > 0) {
        // Motorista com 1+ boxes vinculados
        targets = driverBoxes.map(db => ({
          key: db.id,
          name: db.name || '—',
          isVirtual: false,
        }))
      } else if (o.driver_id || o.driver_name) {
        // Motorista sem box vinculado → card virtual "Definir BOX" por motorista
        const driverKey = o.driver_id || o.driver_name
        targets = [{
          key: `__definir__:${driverKey}`,
          name: '🆕 Definir BOX',
          isVirtual: true,
        }]
      } else {
        // Sem motorista E sem box vinculado: backend deveria ter populado
        // `driver_boxes` com o(s) box(es) accepts_no_driver=true. Se chegou
        // vazio aqui, é porque NENHUM box accepts_no_driver foi cadastrado
        // ainda — cai no card virtual de alerta.
        targets = [{
          key: '__no_driver__',
          name: '🆓 Sem motorista · falta box accepts_no_driver',
          isVirtual: true,
        }]
      }

      // 2) Totais do pedido (independente de em qual box conta — mesmos valores)
      const rawTotal = Number(o.volumes_count) || (o.volumes?.length || 1)
      const embarcadosCount = (o.volumes || []).filter(volumeIsEmbarked).length
      const expectedForOrder = Math.max(0, rawTotal - embarcadosCount)

      // 3) Pra cada box target, anexa o pedido
      for (const t of targets) {
        const b = ensureBox(t.key, t.name, t.isVirtual)

        // volumes deste pedido que estão FISICAMENTE NESTE box (id match)
        // Pra cards virtuais ('Definir BOX', 'Sem motorista'), não há box id real → 0
        const scannedHere = t.isVirtual
          ? 0
          : (o.volumes || []).filter(v => volumeActiveInBox(v) && volumeBoxId(v) === t.key).length

        b.rawOrders.push(o)
        b.orders.push({
          id: o.id,
          order_number: o.order_number,
          recipient_name: cleanRecipientName(o.recipient_name || ''),
          driver_name: extractDriverName(o.driver_name || '') || 'SEM MOTORISTA',
          total: expectedForOrder, scanned: scannedHere,
          weight: Number(o.weight_kg) || 0,
        })
        b.totalScanned += scannedHere
        b.totalExpected += expectedForOrder
        b.weightKg += Number(o.weight_kg) || 0
      }
    }

    // Ordena: cards reais primeiro (por nome) → "Definir BOX" → "(Sem motorista)"
    const boxes = Object.values(boxesMap).sort((a, b) => {
      const pri = (x) => x.key === '__no_driver__' ? 3
                      : x.isVirtual                ? 2
                      : 1
      if (pri(a) !== pri(b)) return pri(a) - pri(b)
      return String(a.name).localeCompare(String(b.name))
    })

    if (!boxes.length) {
      return (
        <EmptyPlaceholder>
          <div className="icon">🗃</div>
          <p>Nenhum box recebeu volumes ainda</p>
        </EmptyPlaceholder>
      )
    }

    return (
      <BoxesGrid>
        {boxes.map(b => {
          const complete = b.totalScanned >= b.totalExpected
          const missing = b.totalExpected - b.totalScanned

          // Agrupa pedidos por motorista (dentro deste box)
          const driverAgg = {}
          for (const o of b.orders) {
            const key = o.driver_name || 'SEM MOTORISTA'
            if (!driverAgg[key]) {
              driverAgg[key] = { name: key, orders: 0, scanned: 0, total: 0 }
            }
            driverAgg[key].orders++
            driverAgg[key].scanned += o.scanned
            driverAgg[key].total   += o.total
          }
          const drivers = Object.values(driverAgg).sort((x, y) => y.orders - x.orders)

          return (
            <BoxCard
              key={b.key}
              onClick={() => {
                setBoxOrdersExpandedId(null)
                setBoxOrdersModal({
                  name: b.name,
                  totalScanned: b.totalScanned,
                  totalExpected: b.totalExpected,
                  rawOrders: b.rawOrders,
                })
              }}
              title="Clique para ver os pedidos vinculados a este box"
            >
              <BoxCardHeader $complete={complete}>
                <span className="name">📦 {b.name}</span>
                <span className="count">{b.totalScanned}/{b.totalExpected}</span>
              </BoxCardHeader>

              <BoxStatsRow>
                <BoxStatCell>
                  <div className="val">{b.orders.length}</div>
                  <div className="lbl">Pedidos</div>
                </BoxStatCell>
                <BoxStatCell $variant={missing > 0 ? 'warn' : 'accent'}>
                  <div className="val">{missing}</div>
                  <div className="lbl">Faltam</div>
                </BoxStatCell>
                <BoxStatCell>
                  <div className="val">{b.weightKg.toFixed(1)}<span style={{ fontSize: 10, color: '#5a6370', marginLeft: 3 }}>kg</span></div>
                  <div className="lbl">Peso total</div>
                </BoxStatCell>
              </BoxStatsRow>

              <BoxDriversList>
                {drivers.map(d => {
                  const dComplete = d.scanned >= d.total
                  const initials = d.name === 'SEM MOTORISTA'
                    ? '?'
                    : d.name.split(' ').slice(0, 2).map(w => w[0] || '').join('').toUpperCase()
                  return (
                    <BoxDriverRow key={d.name} $complete={dComplete}>
                      <span className="ini">{initials}</span>
                      <div style={{ minWidth: 0 }}>
                        <div className="name" title={d.name}>{d.name}</div>
                        <div className="sub">{d.orders} pedido{d.orders !== 1 ? 's' : ''}</div>
                      </div>
                      <span className="count">{d.scanned}/{d.total}</span>
                    </BoxDriverRow>
                  )
                })}
              </BoxDriversList>

              {/* Atalho: embarca este box sem sair do Monta Carga.
                  Cards virtuais ('Definir BOX' / 'Sem motorista') não têm box
                  real no backend, então não oferecem o botão. */}
              {!b.isVirtual && (
                <BoxModalBtn
                  $primary
                  type="button"
                  title="Embarcar os volumes deste box sem sair do Monta Carga"
                  style={{ width: '100%' }}
                  onClick={(event) => {
                    event.stopPropagation()
                    setEmbarkBoxModal({ id: b.key, name: b.name })
                  }}
                >
                  🚚 Embarcar Box
                </BoxModalBtn>
              )}
            </BoxCard>
          )
        })}
      </BoxesGrid>
    )
  }

  // ─── MAIN RENDER ──────────────────────────────────────────────────────────
  const hasData = deliveries.length > 0

  useEffect(() => {
    verifyAuth()
  }, []) // eslint-disable-line

  // Carrega pedidos do backend assim que o auth é validado
  useEffect(() => {
    if (auth === 'authorized') fetchOrders()
  }, [auth, fetchOrders])

  // Auto-foca o input da aba ativa quando o auth está pronto.
  // Depende de `auth` pra rodar SÓ depois que o JSX renderizou — antes disso
  // o ref.current é null e .focus() não faz nada.
  useEffect(() => {
    if (auth !== 'authorized') return
    if (activeTab === 'consulta' || activeTab === 'conference') {
      const t = setTimeout(armScanner, 120)
      return () => clearTimeout(t)
    }
  }, [auth, activeTab, confBox, armScanner])



  return (
    <>
      <Dashboard title="Monta Carga">
        {auth === 'denied' && <Denied />}
        {
          auth === 'authorized' && <>
            <GlobalHomeStyles />
            <PageWrap>
              {/* Cabeçalho com logo e status */}
              <Header>
                <Logo><span>santo</span>Log</Logo>
                <HeaderStatus>
                  <StatusDot $active={hasData} />
                  <span>
                    {loadingOrders
                      ? 'Carregando pedidos…'
                      : hasData
                        ? `${deliveries.length} pedido${deliveries.length !== 1 ? 's' : ''} · status: coletado/no box`
                        : 'Nenhum pedido coletado · importe na tela Pedidos'}
                  </span>
                  <button
                    type="button"
                    onClick={fetchOrders}
                    disabled={loadingOrders}
                    style={{
                      marginLeft: 12,
                      background: 'transparent',
                      border: '1px solid #2a3040',
                      color: '#8a95a3',
                      padding: '4px 10px',
                      borderRadius: 6,
                      fontSize: 10,
                      cursor: 'pointer',
                      fontFamily: "'DM Sans',sans-serif",
                    }}
                  >
                    {loadingOrders ? '…' : '↻ Atualizar'}
                  </button>
                </HeaderStatus>
              </Header>

              {/* Barra de navegação entre abas */}
              <NavBar>
                {TABS.map(tab => (
                  <NavTab
                    key={tab.id}
                    $active={activeTab === tab.id}
                    onClick={() => setActiveTab(tab.id)}
                  >
                    {tab.label}
                  </NavTab>
                ))}
              </NavBar>

              {/* Conteúdo principal conforme aba ativa */}
              <MainContent key={activeTab}>
                {activeTab === 'consulta' && renderConsultaTab()}
                {activeTab === 'conference' && renderConferenceTab()}
                {activeTab === 'entregas' && renderEntregasTab()}
                {activeTab === 'boxes' && renderBoxesTab()}
              </MainContent>
            </PageWrap>

            {/* Modal genérico de feedback (error / warning / success) */}
            <FeedbackModal
              visible={confModal.open}
              kind={confModal.kind}
              title={confModal.title}
              message={confModal.message}
              onClose={closeConfModal}
            >
              {confModal.correct_box && (
                <FeedbackExtra>
                  <div className="lbl">Box correto</div>
                  <div className="val">📦 {confModal.correct_box.name}</div>
                  {confModal.correct_box.barcode && (
                    <div className="meta">código {confModal.correct_box.barcode}</div>
                  )}
                  {confModal.order && (
                    <div className="meta">
                      NF {confModal.order.order_number}
                      {confModal.order.driver_name && ` · motorista ${confModal.order.driver_name}`}
                    </div>
                  )}
                </FeedbackExtra>
              )}
            </FeedbackModal>

            {/* Toast de notificação */}
            <Toast $visible={toast.visible} $error={toast.isError}>
              {toast.message}
            </Toast>

            {/* Modal: pedidos vinculados a um box (click no BoxCard) */}
            <BoxOrdersModalOverlay
              $visible={!!boxOrdersModal}
              onClick={() => setBoxOrdersModal(null)}
            >
              {boxOrdersModal && (
                <BoxOrdersModalBox onClick={e => e.stopPropagation()}>
                  <BoxOrdersModalHeader>
                    <div className="info">
                      <div className="name">📦 {boxOrdersModal.name}</div>
                      <div className="meta">
                        {boxOrdersModal.rawOrders.length} pedido{boxOrdersModal.rawOrders.length !== 1 ? 's' : ''} · {boxOrdersModal.totalScanned}/{boxOrdersModal.totalExpected} volumes coletados
                      </div>
                    </div>
                    <button type="button" className="close" onClick={() => setBoxOrdersModal(null)} title="Fechar (Esc)">✕</button>
                  </BoxOrdersModalHeader>

                  <BoxOrdersModalBody>
                    <AccordionList>
                      {(() => {
                        // Ordem desejada: aguardando_recebimento → coletado_parcial → coletado → outros
                        const STATUS_ORDER = {
                          aguardando_recebimento: 0,
                          coletado_parcial:       1,
                          coletado:               2,
                        }
                        const sorted = [...boxOrdersModal.rawOrders].sort((a, b) => {
                          const pa = STATUS_ORDER[a.status] ?? 99
                          const pb = STATUS_ORDER[b.status] ?? 99
                          if (pa !== pb) return pa - pb
                          // empate: ordena por número do pedido pra estabilidade visual
                          return String(a.order_number || '').localeCompare(String(b.order_number || ''))
                        })
                        return sorted
                      })().map(o => {
                        const total = Number(o.volumes_count) || (o.volumes?.length || 1)
                        const scannedVols = (o.volumes || []).filter(volumeInBox)
                        const scanned = scannedVols.length
                        const expanded = boxOrdersExpandedId === o.id
                        const complete = scanned >= total
                        const partial = scanned > 0 && scanned < total
                        const byNumber = {}
                        for (const v of (o.volumes || [])) {
                          const n = volumeNumber(v)
                          if (n != null) byNumber[n] = v
                        }
                        const slots = Array.from({ length: total }, (_, i) => {
                          const num = i + 1
                          const v = byNumber[num] || null
                          return {
                            num,
                            rastreio: v?.rastreio || null,
                            box_name: volumeBoxNameFor(v, o.driver_boxes),
                            in_box: volumeActiveInBox(v),
                            embarked: volumeIsEmbarked(v),
                            first_scanned_at: v?.first_scanned_at || null,
                          }
                        })
                        return (
                          <AccordionItem
                            key={o.id}
                            style={o.has_reentrega ? {
                              border: '2px solid #f59e0b',
                              borderRadius: 8,
                              background: 'rgba(245, 158, 11, 0.06)',
                              boxShadow: '0 0 0 1px rgba(245, 158, 11, 0.2) inset',
                            } : undefined}
                          >
                            <AccordionHeader
                              $expanded={expanded}
                              $complete={complete}
                              $partial={partial}
                              onClick={() => setBoxOrdersExpandedId(prev => prev === o.id ? null : o.id)}
                              type="button"
                            >
                              <span className="nf">
                                {o.sequence_order != null && (
                                  <span
                                    title={`Sequência de entrega #${o.sequence_order}`}
                                    style={{
                                      marginRight: 6,
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      padding: '1px 5px',
                                      borderRadius: 4,
                                      fontSize: 10,
                                      fontWeight: 700,
                                      background: '#1e293b',
                                      color: '#94a3b8',
                                      border: '1px solid #334155',
                                      fontFamily: "'Space Mono', monospace",
                                      verticalAlign: 'middle',
                                    }}
                                  >
                                    #{o.sequence_order}
                                  </span>
                                )}
                                {o.order_number || '—'}
                                {o.has_reentrega && (
                                  <span
                                    title="Pedido importado em reentrega"
                                    style={{
                                      marginLeft: 6,
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      padding: '2px 6px',
                                      borderRadius: 4,
                                      fontSize: 10,
                                      fontWeight: 700,
                                      letterSpacing: 0.3,
                                      background: '#f59e0b',
                                      color: '#1f2937',
                                      border: '1px solid #d97706',
                                      verticalAlign: 'middle',
                                    }}
                                  >
                                    ⚠ REENTREGA
                                  </span>
                                )}
                              </span>
                              <span className="recipient" title={o.recipient_name || ''}>
                                {cleanRecipientName(o.recipient_name || '—')}
                              </span>
                              <span className="driver">
                                🚛 {extractDriverName(o.driver_name || '') || 'SEM MOTORISTA'}
                                {o.route ? ` · ${o.route}` : ''}
                              </span>
                              <AccordionStatusBadge $status={o.status}>
                                {o.status === 'aguardando_recebimento' ? 'Ag. Receb.' :
                                 o.status === 'coletado_parcial' ? 'Parcial' :
                                 o.status === 'coletado' ? 'Coletado' :
                                 o.status === 'embarcado' ? 'Embarcado' :
                                 o.status === 'entregue' ? 'Entregue' :
                                 o.status === 'nao_embarcado' ? 'Não Embarcado' : o.status}
                              </AccordionStatusBadge>
                              <span className="count">{scanned}/{total}</span>
                              <span className="caret">▾</span>
                            </AccordionHeader>

                            {expanded && (
                              <AccordionBody>
                                <VolumeGrid>
                                  {slots.map(slot => (
                                    <VolumeChip key={slot.num} $scanned={slot.in_box} $embarked={slot.embarked}>
                                      <span className="num">V{slot.num}</span>
                                      <div className="body">
                                        <div className="rastreio">{slot.rastreio || '—'}</div>
                                        <div className="box">
                                          {slot.embarked
                                            ? `🚛 Embarcado${slot.box_name ? ` · saiu do ${slot.box_name}` : ''}`
                                            : slot.in_box
                                              ? `📦 ${slot.box_name || boxOrdersModal.name}`
                                              : 'Aguardando bipagem'}
                                        </div>
                                        {slot.first_scanned_at && (
                                          <div className="box">🔵 1º beep {formatBeepShort(slot.first_scanned_at)}</div>
                                        )}
                                      </div>
                                    </VolumeChip>
                                  ))}
                                </VolumeGrid>
                              </AccordionBody>
                            )}
                          </AccordionItem>
                        )
                      })}
                    </AccordionList>
                  </BoxOrdersModalBody>
                </BoxOrdersModalBox>
              )}
            </BoxOrdersModalOverlay>

            {/* Atalho de embarque a partir do card do box */}
            <EmbarkBoxModal
              box={embarkBoxModal}
              onClose={() => setEmbarkBoxModal(null)}
              onEmbarked={fetchOrders}
            />
          </>}
      </Dashboard>
    </>
  )
}
